import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Car, 
  Phone,
  MessageCircle,
  Loader2,
  Check,
  Calculator,
  DollarSign,
  Calendar,
  Percent,
  Globe,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Info,
  Building2,
  Bot,
  X,
  FileText,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-filled WhatsApp message for maquinaria
const WHATSAPP_DEFAULT_MESSAGE = "Hola, vi la página de maquinaria y quisiera más información.";

// Business configurations (same as CatalogPage)
const BUSINESS_CONFIG = {
  automotriz: {
    id: "automotriz",
    name: "HEBEH Automotriz",
    slug: "automotriz",
    icon: Car,
    accentColor: "#3B82F6",
    accentColorSecondary: "#F09A21",
    whatsapp: "5513181745",
    phone: "55 1318 1745",
    itemLabel: "vehículo",
    website: "automotriz.hebeh.com.mx"
  },
  maquinaria: {
    id: "maquinaria",
    name: "HEBEH Maquinaria y Transporte",
    slug: "maquinaria",
    icon: Building2,
    accentColor: "#F09A21",
    accentColorSecondary: "#1E4C7A",
    whatsapp: "5513181745",
    phone: "55 1318 1745",
    itemLabel: "equipo",
    // Logo oficial
    logoUrl: "https://customer-assets.emergentagent.com/job_5eca9e74-1690-4426-bde8-88577910a114/artifacts/zt71l0ae_LOGO%20OFICIAL%20BLANCO%20HEBEH.png"
  }
};

const getBusinessFromContext = (slug, vehicleBusinessId) => {
  // Priority: URL slug > vehicle's business_id > default
  if (slug && BUSINESS_CONFIG[slug]) {
    return BUSINESS_CONFIG[slug];
  }
  if (vehicleBusinessId && BUSINESS_CONFIG[vehicleBusinessId]) {
    return BUSINESS_CONFIG[vehicleBusinessId];
  }
  
  // Check hostname
  const hostname = window.location.hostname;
  if (hostname.includes("maquinaria")) {
    return BUSINESS_CONFIG.maquinaria;
  }
  
  return BUSINESS_CONFIG.automotriz;
};

// Image Gallery Carousel Component - Simplified for Maquinaria
const ImageGallery = ({ images, webImage, vehicleName, isMaquinaria }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Construir array de imágenes según el negocio
  const buildImageList = () => {
    const list = [];
    
    // Agregar imagen Web primero (sin texto, limpia)
    if (webImage) {
      list.push({ name: "Catálogo", url: webImage, isWeb: true });
    }
    
    // Agregar imágenes de la galería
    if (images && images.length > 0) {
      images.forEach(img => {
        if (img?.url) {
          // Para maquinaria: solo "Trabajo" (excluir Hook y Ficha)
          if (isMaquinaria) {
            const nameLower = img.name?.toLowerCase() || "";
            if (nameLower === "trabajo") {
              list.push(img);
            }
          } else {
            // Para automotriz: excluir solo Ficha
            if (img.name?.toLowerCase() !== "ficha") {
              list.push(img);
            }
          }
        }
      });
    }
    
    return list;
  };

  const validImages = buildImageList();

  if (validImages.length === 0) {
    return (
      <div className="aspect-[4/3] bg-slate-800 rounded-xl flex items-center justify-center">
        <Car className="w-24 h-24 text-slate-600" />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % validImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
  };

  return (
    <div className="relative bg-slate-900 rounded-lg overflow-hidden group">
      <img 
        src={validImages[currentIndex]?.url} 
        alt={`${vehicleName} - ${validImages[currentIndex]?.name}`}
        className="w-full h-auto object-contain"
        data-testid="gallery-main-image"
      />
      
      {/* Image name badge */}
      <div className="absolute top-3 left-3 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
        {validImages[currentIndex]?.name}
      </div>

      {/* Navigation arrows - Always visible if more than 1 image */}
      {validImages.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white transition-all"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white transition-all"
            data-testid="gallery-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Image counter - bottom center */}
      {validImages.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {validImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? "bg-white w-6" 
                  : "bg-white/50 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// WhatsApp floating button with pre-filled message
// Floating Buttons Component (WhatsApp, Chatbot, Scroll to Top)
const FloatingButtons = ({ vehicleName, business }) => {
  const config = business || BUSINESS_CONFIG.automotriz;
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  
  const message = config.id === "maquinaria" 
    ? WHATSAPP_DEFAULT_MESSAGE 
    : `Hola, me interesa el ${vehicleName}`;

  // Show scroll to top button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <>
      {/* Floating buttons container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end">
        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 shadow-lg hover:scale-110 transition-all"
            data-testid="scroll-top-btn"
          >
            <ChevronUp className="h-6 w-6 text-white" />
          </button>
        )}

        {/* Chatbot Button */}
        <button
          onClick={() => setShowChatbot(!showChatbot)}
          className="flex h-14 w-14 items-center justify-center rounded-full shadow-lg hover:scale-110 transition-transform"
          style={{ backgroundColor: config.accentColor }}
          data-testid="chatbot-float-btn"
        >
          <Bot className="h-7 w-7 text-white" />
        </button>

        {/* WhatsApp Button */}
        <a
          href={`https://api.whatsapp.com/send?phone=${config.whatsapp}&text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:scale-110 transition-transform"
          data-testid="whatsapp-float-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>

      {/* Chatbot Modal */}
      {showChatbot && (
        <div className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-[#0B1623] border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between" style={{ backgroundColor: config.accentColor }}>
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">Asistente HEBEH</span>
            </div>
            <button onClick={() => setShowChatbot(false)} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 h-64 overflow-y-auto">
            <div className="bg-slate-800 rounded-lg p-3 mb-3">
              <p className="text-sm text-slate-300">
                ¡Hola! 👋 Soy el asistente virtual de HEBEH. ¿En qué puedo ayudarte hoy?
              </p>
            </div>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors">
                📋 Quiero cotizar un equipo
              </button>
              <button className="w-full text-left px-3 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors">
                💰 Información de financiamiento
              </button>
              <button className="w-full text-left px-3 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors">
                🔧 Servicio técnico
              </button>
              <button className="w-full text-left px-3 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition-colors">
                📞 Hablar con un asesor
              </button>
            </div>
          </div>
          <div className="p-3 border-t border-slate-700">
            <a
              href={`https://api.whatsapp.com/send?phone=${config.whatsapp}&text=${encodeURIComponent(message)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 bg-[#25D366] hover:bg-[#20BD5C] rounded-lg text-white text-sm font-medium transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Continuar en WhatsApp
            </a>
          </div>
        </div>
      )}
    </>
  );
};

// Financing Calculator Component - Horizontal Layout
const FinancingCalculator = ({ price, interestRate, vehicleName, business }) => {
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [termMonths, setTermMonths] = useState(60);
  const config = business || BUSINESS_CONFIG.automotriz;
  const isMaquinaria = config.id === "maquinaria";

  const calculations = useMemo(() => {
    const numPrice = parseFloat(price?.replace(/[,$]/g, "") || 0);
    const downPaymentAmount = (numPrice * downPaymentPercent) / 100;
    const financedAmount = numPrice - downPaymentAmount;
    const monthlyRate = interestRate / 100 / 12;
    
    // PMT formula
    const monthlyPayment = financedAmount > 0 
      ? (financedAmount * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
        (Math.pow(1 + monthlyRate, termMonths) - 1)
      : 0;

    return {
      price: numPrice,
      downPaymentAmount,
      financedAmount,
      monthlyPayment
    };
  }, [price, downPaymentPercent, termMonths, interestRate]);

  const formatCurrency = (value) => {
    return value.toLocaleString("es-MX", { 
      style: "currency", 
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const termOptions = [36, 48, 60, 72];

  const whatsappMessage = isMaquinaria 
    ? `${WHATSAPP_DEFAULT_MESSAGE} Me interesa ${vehicleName}. Mi simulación: Enganche ${downPaymentPercent}% (${formatCurrency(calculations.downPaymentAmount)}), ${termMonths} meses, mensualidad ${formatCurrency(calculations.monthlyPayment)}`
    : `Hola, me interesa el ${vehicleName}. Mi simulación: Enganche ${downPaymentPercent}% (${formatCurrency(calculations.downPaymentAmount)}), ${termMonths} meses, mensualidad ${formatCurrency(calculations.monthlyPayment)}`;

  // Dynamic labels
  const buttonLabel = isMaquinaria ? "Cotizar este equipo" : "Cotizar este auto";

  return (
    <div className="glass-card p-6" data-testid="financing-calculator">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Calculator className="w-5 h-5" style={{ color: config.accentColor }} />
          Simulador de Financiamiento
        </h3>
        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded">
          Tasa: {interestRate}% anual
        </span>
      </div>

      {/* Horizontal Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Column 1: Down Payment */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">
            Enganche ({downPaymentPercent}%)
          </label>
          <input
            type="range"
            min="5"
            max="50"
            step="5"
            value={downPaymentPercent}
            onChange={(e) => setDownPaymentPercent(parseInt(e.target.value))}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: config.accentColor }}
            data-testid="down-payment-slider"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>5%</span>
            <span style={{ color: config.accentColor }} className="font-medium">{formatCurrency(calculations.downPaymentAmount)}</span>
            <span>50%</span>
          </div>
        </div>

        {/* Column 2: Term Selection */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">Plazo (meses)</label>
          <div className="grid grid-cols-4 gap-1">
            {termOptions.map((term) => (
              <button
                key={term}
                onClick={() => setTermMonths(term)}
                className={`py-2 rounded-lg text-sm font-medium transition-all ${
                  termMonths === term
                    ? "text-white"
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                }`}
                style={termMonths === term ? { backgroundColor: config.accentColor } : {}}
                data-testid={`term-btn-${term}`}
              >
                {term}
              </button>
            ))}
          </div>
        </div>

        {/* Column 3: Monthly Payment Result */}
        <div className="bg-[#0f213b] border border-slate-700 rounded-lg p-4">
          <p className="text-xs text-slate-400 mb-1">Mensualidad estimada</p>
          <p className="text-2xl font-bold" style={{ color: config.accentColor }} data-testid="monthly-payment">
            {formatCurrency(calculations.monthlyPayment)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {formatCurrency(calculations.financedAmount)} a {termMonths} meses
          </p>
        </div>

        {/* Column 4: CTA Button */}
        <div className="flex items-center">
          <a
            href={`https://api.whatsapp.com/send?phone=${config.whatsapp}&text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-4 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#20BD5C] transition-colors"
            data-testid="calculator-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            {buttonLabel}
          </a>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-slate-500 mt-4 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        Simulación referencial. El crédito es otorgado por financieras aliadas. 
        Tasa de referencia {interestRate}% nominal anual. Precios sujetos a disponibilidad.
      </p>
    </div>
  );
};

// Contact Form Component
const ContactForm = ({ vehicleId, vehicleName }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      setError("Por favor ingresa tu nombre y teléfono");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/public/leads`, {
        ...formData,
        vehicle_id: vehicleId,
        vehicle_name: vehicleName,
        source: "vehicle_detail"
      });
      setSuccess(true);
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      setError("Error al enviar. Por favor intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="glass-card p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-400" />
        </div>
        <h4 className="text-xl font-semibold text-white mb-2">¡Mensaje enviado!</h4>
        <p className="text-slate-400">
          Nos pondremos en contacto contigo pronto. También puedes escribirnos directamente por WhatsApp.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-card p-6" data-testid="contact-form">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-orange-400" />
        ¿Te interesa este vehículo?
      </h3>
      <p className="text-slate-400 text-sm mb-4">
        Déjanos tus datos y un asesor te contactará
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Tu nombre *"
            className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="Tu teléfono *"
            className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="contact-phone-input"
          />
        </div>
        <div>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Tu email (opcional)"
            className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="contact-email-input"
          />
        </div>
        <div>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Mensaje (opcional)"
            rows={3}
            className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none resize-none"
            data-testid="contact-message-input"
          />
        </div>

        {error && (
          <p className="text-red-400 text-sm">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="contact-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            "Enviar"
          )}
        </button>
      </form>
    </div>
  );
};

import VehicleDetailPremium from "./premium/VehicleDetailPremium";

function VehicleDetailPageLegacy() {
  const { vehicleId, businessSlug } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [interestRate, setInterestRate] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  // Versiones hermanas (mismo brand+model+year). Cambia specs/precio al tocar cada tab.
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);

  useEffect(() => {
    fetchVehicle();
  }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/public/vehicles/${vehicleId}`);
      if (response.data.success) {
        setVehicle(response.data.vehicle);
        setInterestRate(response.data.interest_rate || 20);
        // Get business from response or URL
        const vehicleBusinessId = response.data.vehicle?.business_id;
        setBusiness(getBusinessFromContext(businessSlug, vehicleBusinessId));
        // Cargar versiones hermanas (ordenadas por precio asc en backend)
        const vs = response.data.versions || [];
        setVersions(vs);
        // Activar por defecto la versión que abrió el usuario si está en la lista;
        // si no, la primera (más barata)
        const currentId = response.data.vehicle?.id;
        const found = vs.find((x) => x.id === currentId);
        setActiveVersionId(found ? currentId : (vs[0]?.id || currentId));
      } else {
        setError("No encontrado");
      }
    } catch (err) {
      console.error("Error fetching vehicle:", err);
      setError("Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  // Get business config - use dynamic data from API if available
  const staticConfig = getBusinessFromContext(businessSlug);
  const config = business ? { ...staticConfig, ...business } : staticConfig;
  const BusinessIcon = staticConfig.icon || Car;
  const vehicleName = vehicle ? `${vehicle.brand} ${vehicle.model_name} ${(versions.find((v) => v.id === activeVersionId)?.version) ?? vehicle.version ?? ""}`.trim() : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1623] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: config.accentColor }} />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-[#0B1623] flex items-center justify-center">
        <div className="text-center">
          <BusinessIcon className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            {error || `${config.itemLabel.charAt(0).toUpperCase() + config.itemLabel.slice(1)} no encontrado`}
          </h2>
          <Link 
            to={`/catalogo/${config.slug}`} 
            className="inline-flex items-center gap-2 mt-4"
            style={{ color: config.accentColor }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    if (!price) return "Consultar";
    const numPrice = parseFloat(price.replace(/[,$]/g, ""));
    return `$${numPrice.toLocaleString("es-MX")}`;
  };

  // Datos activos (versión seleccionada). Imágenes SIEMPRE se toman del vehicle base
  // (versión que abrió el usuario) — no cambian al mover tabs. Sólo cambian precio,
  // specs, ficha técnica y el nombre en los botones CTA.
  const activeVersion = versions.find((v) => v.id === activeVersionId);
  const activePrice = activeVersion?.price ?? vehicle.price;
  const activeSpecs = (activeVersion?.specifications && activeVersion.specifications.length > 0)
    ? activeVersion.specifications
    : vehicle.specifications;
  const activeSpecSheet = activeVersion?.spec_sheet_url ?? vehicle.spec_sheet_url;
  const activeVersionName = activeVersion?.version ?? vehicle.version;
  const activeVehicleName = `${vehicle.brand} ${vehicle.model_name} ${activeVersionName || ""}`.trim();
  const showVersionTabs = versions.length > 1;

  return (
    <div className="min-h-screen bg-[#0B1623]">
      {/* Header - Compacto pero con logo visible */}
      <header className="border-b border-slate-800/50 bg-[#0B1623]/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            <Link to={`/catalogo/${config.slug}`} className="flex items-center gap-4">
              {/* Logo oficial */}
              {config.logoUrl ? (
                <img 
                  src={config.logoUrl} 
                  alt="HEBEH" 
                  className="h-10 md:h-12 w-auto"
                  data-testid="header-logo"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColorSecondary})` }}
                >
                  <BusinessIcon className="w-5 h-5 text-white" />
                </div>
              )}
            </Link>
            
            {/* Título y contacto a la derecha */}
            <div className="flex items-center gap-4">
              <h1 className="hidden md:block font-montserrat text-sm md:text-base font-semibold text-white/80 tracking-wide">
                {config.id === "maquinaria" ? "Maquinaria y Transporte" : "AUTOMOTRIZ"}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link 
            to={`/catalogo/${config.slug}`} 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-catalog"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al catálogo
          </Link>
        </nav>

        {/* Row 1: Image + Details (same height, aligned) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Vehicle Gallery (2/5 width) */}
          <div className="lg:col-span-2">
            <div className="glass-card p-3 h-full">
              <ImageGallery 
                images={vehicle.web_gallery_images?.length > 0 ? vehicle.web_gallery_images : vehicle.gallery_images}
                webImage={vehicle.web_image}
                vehicleName={vehicleName}
                isMaquinaria={config.id === "maquinaria"}
              />
            </div>
          </div>

          {/* Right Column - Vehicle Details & Specs (3/5 width) */}
          <div className="lg:col-span-3">
            <div className="glass-card p-6 h-full flex flex-col" data-testid="vehicle-details">
              {/* Header */}
              <div className="mb-4">
                <p className="text-sm font-medium uppercase tracking-wide" style={{ color: config.accentColor }}>
                  {vehicle.brand || "Vehículo"}
                </p>
                <h2 className="text-2xl lg:text-3xl font-bold text-white mt-1" data-testid="vehicle-title">
                  {vehicle.model_name}
                </h2>
                <p className="text-lg text-slate-400">
                  {activeVersionName} {vehicle.year && `• ${vehicle.year}`}
                </p>
              </div>

              {/* Version Tabs — visibles cuando hay más de una versión */}
              {showVersionTabs && (
                <div className="mb-4" data-testid="version-tabs">
                  <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Elige tu versión</p>
                  <div className="flex flex-wrap gap-2">
                    {versions.map((ver) => {
                      const isActive = ver.id === activeVersionId;
                      const priceNum = parseFloat(String(ver.price || "0").replace(/[,$]/g, "")) || 0;
                      return (
                        <button
                          key={ver.id}
                          onClick={() => setActiveVersionId(ver.id)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                            isActive
                              ? "text-white"
                              : "bg-slate-800/60 text-slate-300 border-slate-700 hover:border-slate-500"
                          }`}
                          style={isActive ? {
                            backgroundColor: `${config.accentColor}22`,
                            borderColor: config.accentColor,
                            color: config.accentColor,
                          } : undefined}
                          data-testid={`version-tab-${ver.id}`}
                        >
                          <div className="text-left leading-tight">
                            <div className="font-semibold">{ver.version || "Base"}</div>
                            {priceNum > 0 && (
                              <div className="text-[11px] opacity-80">${priceNum.toLocaleString("es-MX")}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Price Row */}
              <div className="flex items-center justify-between py-4 border-t border-b border-slate-700/50">
                <div>
                  <p className="text-sm text-slate-500">Precio</p>
                  <p className="text-2xl lg:text-3xl font-bold text-white" data-testid="vehicle-price">
                    {formatPrice(activePrice)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Financiamiento</p>
                  <p className="text-lg font-medium text-green-400">Disponible</p>
                </div>
              </div>

              {/* Specifications - Grow to fill space */}
              {activeSpecs && activeSpecs.length > 0 && (
                <div className="mt-4 flex-1">
                  <h4 className="text-lg font-semibold text-white mb-3">Especificaciones</h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {activeSpecs.map((spec, idx) => (
                      <li 
                        key={idx} 
                        className="flex items-start gap-2 text-slate-300"
                        data-testid={`spec-${idx}`}
                      >
                        <Check className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: config.accentColor }} />
                        <span className="text-sm">{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Spec Sheet Download Button */}
              {activeSpecSheet && (
                <div className="mt-4">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${activeSpecSheet}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors text-sm font-medium"
                    data-testid="download-spec-sheet"
                  >
                    <FileText className="w-4 h-4" />
                    Descargar Ficha Técnica
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* CTA Buttons - At bottom */}
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-700/50">
                <a
                  href={`https://api.whatsapp.com/send?phone=${config.whatsapp}&text=${encodeURIComponent(config.id === "maquinaria" ? `${WHATSAPP_DEFAULT_MESSAGE} Me interesa: ${activeVehicleName}` : `Hola, me interesa el ${activeVehicleName}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#20BD5C] transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
                <a
                  href={`tel:${config.whatsapp}`}
                  className="flex items-center justify-center gap-2 py-3 rounded-lg border text-white font-semibold hover:bg-white/10 transition-colors"
                  style={{ borderColor: config.accentColor, color: config.accentColor }}
                >
                  <Phone className="w-5 h-5" />
                  Llamar
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Financing Calculator - Full Width */}
        <div className="mt-6">
          <FinancingCalculator 
            price={activePrice} 
            interestRate={interestRate}
            vehicleName={activeVehicleName}
            business={config}
          />
        </div>

        {/* Row 3: Contact Form - Full Width */}
        <div className="mt-6">
          <ContactForm 
            vehicleId={vehicle.id}
            vehicleName={activeVehicleName}
          />
        </div>
      </main>

      {/* Footer - Simple */}
      <footer className="border-t border-slate-800/50 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs text-slate-600">
            © 2026 HEBEH. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Floating Buttons (WhatsApp, Chatbot, Scroll to Top) */}
      <FloatingButtons vehicleName={vehicleName} business={config} />
    </div>
  );
}


// Router: Automotriz uses the premium redesign; other verticals keep the legacy detail page.
// Detección por hostname: si estamos en automotriz.hebeh.com.mx o en la ruta /:brand/:model
// (sin businessSlug), asumimos automotriz y renderizamos la premium.
export default function VehicleDetailPage() {
  const { businessSlug, brandSlug } = useParams();
  const slug = (businessSlug || "").toLowerCase();
  if (slug === "automotriz") return <VehicleDetailPremium />;
  // Ruta corta /:brandSlug/:modelSlug (subdominio automotriz.*)
  if (brandSlug && !businessSlug) {
    const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
    if (host.startsWith("automotriz") || host === "automotriz") {
      return <VehicleDetailPremium />;
    }
  }
  return <VehicleDetailPageLegacy />;
}
